import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { ModalsManager } from '@/components/modals/modalsManager';
import { Button, Text, Tooltip, XStack, YStack } from '@/components/ui';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/Separator';
import { useHashRoute } from '@/hooks/useHashRoute';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/utils/analytics';
import { handleExternalUrlClick } from '@/utils/platform';

import { useElectronContextCollection } from './context-collection-provider';
import {
  type ContextCollectionStateDetails,
  getStateDetails,
  mapToUIState,
} from './context-collection.config';
import * as contextCollectionStore from './context-collection.store';
import {
  CONTEXT_COLLECTOR_STATE,
  PauseContextCollectionDurations,
} from './context-collection.types';
import { deleteContext } from './context-collection.utils';
import { ContextDeleteModal } from './context-delete-modal';

const CONTEXT_AWARENESS_URL = 'https://support.littlebird.ai/docs/context/';

const MENU_COLLISION_PADDING = 8;

const ContextHelperPanel = observer(() => {
  const showContextHelper = contextCollectionStore.useShowContextHelper();

  if (!showContextHelper) return null;

  return (
    <YStack
      className={cn(
        'gap-1.5 pl-3 pr-1.5 pt-2 pb-4 mx-2 my-1',
        'dark:bg-gradient-to-tr dark:from-surface-active dark:to-surface-hover',
        'bg-surface',
        'border border-border-strongest',
        'rounded-lg',
        'shadow-sm'
      )}
      onClick={(e) => e.stopPropagation()}
    >
      <XStack className="items-center justify-between gap-2">
        <XStack className="items-center gap-1.5 flex-1">
          <Text className="text-sm font-medium text-foreground">
            Context Awareness
          </Text>
        </XStack>
        <button
          onClick={(e) => {
            e.stopPropagation();
            contextCollectionStore.setShowContextHelper(false);
          }}
          className={cn(
            'flex items-center justify-center',
            'size-5 rounded-full',
            'hover:bg-surface-hover',
            'transition-colors',
            'shrink-0'
          )}
          aria-label="Dismiss explanation panel"
        >
          <Icons.cancel className="size-3 text-foreground-subtle" />
        </button>
      </XStack>
      <YStack className="gap-2">
        <Text className="text-xs text-foreground-subtle leading-normal mr-2">
          Littlebird remembers your work across apps, no integrations needed.
        </Text>
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleExternalUrlClick(CONTEXT_AWARENESS_URL);
          }}
          className={cn(
            'text-xs text-foreground-subtle',
            'hover:text-foreground',
            'transition-colors',
            'underline underline-offset-4',
            'text-left',
            'flex items-center gap-1'
          )}
        >
          Learn more
          <Icons.externalLink2 className="size-3 text-foreground-muted shrink-0" />
        </button>
      </YStack>
    </YStack>
  );
});

interface ContextCollectionMenuProps {
  menuWidth?: number;
  menuAlignOffset?: number;
}

const ContextCollectionMenu = observer(
  ({ menuWidth, menuAlignOffset }: ContextCollectionMenuProps) => {
    const { setHash } = useHashRoute();
    const [open, setOpen] = useState(false);

    const {
      status,
      timerState,
      accessibilityPermissions,
      supportsContextCollection,
      isLoading,
      startContextCollection,
      stopContextCollection,
      requestAccessibilityPermissions,
      refreshAccessibilityPermissions,
      forceStatusCheck,
      pauseContextCollection,
      disableContextCollection,
      resumeContextCollection,
    } = useElectronContextCollection();

    const [error, setError] = useState<string | null>(null);
    const [optimisticState, setOptimisticState] = useState<{
      state: CONTEXT_COLLECTOR_STATE;
      pauseRemainingMs?: number;
    } | null>(null);

    // Map to existing UI state
    const contextCollectionState = mapToUIState(
      status,
      accessibilityPermissions,
      supportsContextCollection,
      false, // isStarting handled in hook
      false // isStopping handled in hook
    );

    // Clear optimistic override once the real state has caught up or failed
    useEffect(() => {
      if (!optimisticState) return;
      if (
        contextCollectionState === optimisticState.state ||
        contextCollectionState ===
          CONTEXT_COLLECTOR_STATE.SOMETHING_WENT_WRONG ||
        contextCollectionState === CONTEXT_COLLECTOR_STATE.UNABLE_TO_START ||
        contextCollectionState === CONTEXT_COLLECTOR_STATE.UNABLE_TO_STOP
      ) {
        setOptimisticState(null);
      }
    }, [contextCollectionState, optimisticState]);

    const displayState = optimisticState?.state ?? contextCollectionState;
    const displayTimerState =
      optimisticState?.pauseRemainingMs !== undefined
        ? { isPaused: true, remainingTimeMs: optimisticState.pauseRemainingMs }
        : timerState;

    const contextCollectionStateDetails = getStateDetails(
      displayState,
      displayTimerState
    );

    // Handle start with error handling
    const handleStartContextCollection = useCallback(async () => {
      try {
        setError(null);
        setOptimisticState({ state: CONTEXT_COLLECTOR_STATE.ENABLED });
        trackEvent('CONTEXT_COLLECTION_STARTED');
        await startContextCollection();
      } catch (err) {
        setOptimisticState(null);
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to start Context Awareness';
        setError(errorMessage);
        console.error('Failed to start Context Awareness:', err);
      }
    }, [startContextCollection]);

    const handleStopContextCollection = useCallback(async () => {
      try {
        setError(null);
        setOptimisticState({ state: CONTEXT_COLLECTOR_STATE.STOPPED });
        trackEvent('CONTEXT_COLLECTION_STOPPED');
        await stopContextCollection();
      } catch (err) {
        setOptimisticState(null);
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to stop Context Awareness';
        setError(errorMessage);
        console.error('Failed to stop Context Awareness:', err);
      }
    }, [stopContextCollection]);

    const handleDisableContextCollection = useCallback(async () => {
      try {
        setError(null);
        trackEvent('CONTEXT_COLLECTION_DISABLED');
        await disableContextCollection();
      } catch (err) {
        setOptimisticState(null);
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to disable Context Awareness';
        setError(errorMessage);
        console.error('Failed to disable Context Awareness:', err);
      }
    }, [disableContextCollection]);

    const handlePauseForMinutes = useCallback(
      async (minutes: number) => {
        try {
          setError(null);
          setOptimisticState({
            state: CONTEXT_COLLECTOR_STATE.PAUSED_TIMED,
            pauseRemainingMs: minutes * 60_000,
          });
          trackEvent('CONTEXT_COLLECTION_PAUSED', { duration: minutes });
          await pauseContextCollection(minutes);
        } catch (err) {
          setOptimisticState(null);
          const errorMessage =
            err instanceof Error
              ? err.message
              : 'Failed to pause Context Awareness';
          setError(errorMessage);
          console.error('Failed to pause Context Awareness:', err);
        }
      },
      [pauseContextCollection]
    );

    const handleResumeContextCollection = useCallback(async () => {
      try {
        setError(null);
        setOptimisticState({ state: CONTEXT_COLLECTOR_STATE.ENABLED });
        trackEvent('CONTEXT_COLLECTION_RESUMED');
        await resumeContextCollection();
      } catch (err) {
        setOptimisticState(null);
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to resume Context Awareness';
        setError(errorMessage);
        console.error('Failed to resume Context Awareness:', err);
      }
    }, [resumeContextCollection]);

    const handleDeleteData = useCallback(async (timeMs: number) => {
      try {
        setError(null);
        await deleteContext(timeMs);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to delete data';
        setError(errorMessage);
        console.error('Failed to delete data:', err);
      }
    }, []);

    // Handle accessibility permissions
    const handleRequestAccessibilityPermissions = useCallback(async () => {
      try {
        setError(null);
        const granted = await requestAccessibilityPermissions();
        if (!granted) {
          setError('Accessibility permissions were not granted');
        }
        trackEvent('PERMISSIONS_REQUESTED');
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to request permissions';
        setError(errorMessage);
        console.error('Failed to request accessibility permissions:', err);
      }
    }, [requestAccessibilityPermissions]);

    // Handle retry for error states
    const handleRetry = useCallback(async () => {
      if (
        contextCollectionState === CONTEXT_COLLECTOR_STATE.UNABLE_TO_START ||
        contextCollectionState === CONTEXT_COLLECTOR_STATE.SOMETHING_WENT_WRONG
      ) {
        await handleStartContextCollection();
      } else if (
        contextCollectionState === CONTEXT_COLLECTOR_STATE.UNABLE_TO_STOP
      ) {
        await handleStopContextCollection();
      } else {
        forceStatusCheck();
      }
    }, [
      contextCollectionState,
      handleStartContextCollection,
      handleStopContextCollection,
      forceStatusCheck,
    ]);

    if (!supportsContextCollection) return null;

    const handleOpenChange = (nextOpen: boolean) => {
      setOpen(nextOpen);
      if (nextOpen) {
        forceStatusCheck();
        refreshAccessibilityPermissions();
      }
    };

    const isPending =
      isLoading ||
      displayState === CONTEXT_COLLECTOR_STATE.STARTING ||
      displayState === CONTEXT_COLLECTOR_STATE.STOPPING;

    return (
      <DropdownMenu.Root open={open} onOpenChange={handleOpenChange}>
        <Tooltip
          label={contextCollectionStateDetails.text}
          side="top"
          sideOffset={6}
        >
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              id="context-collection-menu-trigger"
              aria-label={`Context Awareness: ${contextCollectionStateDetails.text}. Open menu to change.`}
              className={cn(
                'flex size-8 shrink-0 items-center justify-center rounded-md',
                'outline-none transition-colors',
                'hover:bg-surface-hover',
                'data-[dropdown-open=true]:bg-surface-hover',
                'focus-visible:ring-2 focus-visible:ring-ring'
              )}
            >
              <CollectionStateIcon
                details={contextCollectionStateDetails}
                isPending={isPending}
              />
            </button>
          </DropdownMenu.Trigger>
        </Tooltip>
        <DropdownMenu.Content
          side="top"
          align="end"
          sideOffset={8}
          alignOffset={menuAlignOffset}
          className="gap-1"
          style={menuWidth ? { width: menuWidth } : undefined}
          collisionPadding={MENU_COLLISION_PADDING}
          onCloseAutoFocus={(event) => event.preventDefault()}
        >
          <ContextHelperPanel />

          {/* Unsupported platform */}
          {contextCollectionState === CONTEXT_COLLECTOR_STATE.UNSUPPORTED && (
            <DropdownMenu.Item disabled>Coming Soon</DropdownMenu.Item>
          )}

          {/* Enabled - show pause option */}
          {contextCollectionState === CONTEXT_COLLECTOR_STATE.ENABLED && (
            <DropdownMenu.Sub>
              <DropdownMenu.SubTrigger
                disabled={isLoading}
                id="pause-context-trigger"
              >
                <Icons.pause className="text-foreground-muted" />
                Pause Context Awareness
              </DropdownMenu.SubTrigger>
              <DropdownMenu.Portal>
                <DropdownMenu.SubContent
                  collisionPadding={MENU_COLLISION_PADDING}
                >
                  <DropdownMenu.Label>
                    Pause Context Awareness for
                  </DropdownMenu.Label>
                  {[
                    PauseContextCollectionDurations.FiveMinutes,
                    PauseContextCollectionDurations.FifteenMinutes,
                    PauseContextCollectionDurations.ThirtyMinutes,
                    PauseContextCollectionDurations.OneHour,
                  ].map((duration) => {
                    const label =
                      duration === 60 ? 'An hour' : `${duration} minutes`;
                    return (
                      <DropdownMenu.Item
                        key={duration}
                        onClick={() => handlePauseForMinutes(duration)}
                        disabled={isLoading}
                        id={`pause-context-${duration}min`}
                      >
                        {label}
                      </DropdownMenu.Item>
                    );
                  })}
                  <DropdownMenu.Separator />
                  <DropdownMenu.Item
                    id="disable-context"
                    onClick={handleDisableContextCollection}
                    disabled={isLoading}
                  >
                    Disable
                  </DropdownMenu.Item>
                </DropdownMenu.SubContent>
              </DropdownMenu.Portal>
            </DropdownMenu.Sub>
          )}

          {(contextCollectionState === CONTEXT_COLLECTOR_STATE.PAUSED_TIMED ||
            contextCollectionState === CONTEXT_COLLECTOR_STATE.STOPPED) && (
            <DropdownMenu.Item
              onClick={handleResumeContextCollection}
              disabled={isLoading}
              id="resume-context"
            >
              {isLoading ? (
                <Button variant="ghost" disabled isLoading className="p-0" />
              ) : (
                <>
                  <Icons.playAlt className="text-foreground-muted" />
                  {contextCollectionState ===
                  CONTEXT_COLLECTOR_STATE.PAUSED_TIMED
                    ? 'Resume Context Awareness'
                    : 'Enable Context Awareness'}
                </>
              )}
            </DropdownMenu.Item>
          )}

          {/* Enabled or any paused variant - show delete data option */}
          {(contextCollectionState === CONTEXT_COLLECTOR_STATE.ENABLED ||
            contextCollectionState === CONTEXT_COLLECTOR_STATE.PAUSED_TIMED ||
            contextCollectionState === CONTEXT_COLLECTOR_STATE.STOPPED) && (
            <DropdownMenu.Sub>
              <DropdownMenu.SubTrigger disabled={isLoading}>
                <Icons.trash className="text-foreground-muted" />
                Delete Data
              </DropdownMenu.SubTrigger>
              <DropdownMenu.Portal>
                <DropdownMenu.SubContent
                  collisionPadding={MENU_COLLISION_PADDING}
                >
                  <DropdownMenu.Label>Delete data for</DropdownMenu.Label>
                  <DropdownMenu.ItemWithConfirm
                    onConfirm={() => handleDeleteData(5 * 60 * 1000)}
                    confirmLabel="Confirm?"
                    ariaLabel="Delete last 5 minutes context"
                    iconClass="hidden"
                  >
                    Last 5 minutes
                  </DropdownMenu.ItemWithConfirm>
                  <DropdownMenu.ItemWithConfirm
                    onConfirm={() => handleDeleteData(15 * 60 * 1000)}
                    confirmLabel="Confirm?"
                    ariaLabel="Delete last 15 minutes context"
                    iconClass="hidden"
                  >
                    Last 15 minutes
                  </DropdownMenu.ItemWithConfirm>
                  <DropdownMenu.Item
                    onClick={() => {
                      ModalsManager.showResolvable(ContextDeleteModal, {});
                    }}
                    disabled={isLoading}
                  >
                    Custom...
                  </DropdownMenu.Item>
                </DropdownMenu.SubContent>
              </DropdownMenu.Portal>
            </DropdownMenu.Sub>
          )}

          {/* Missing accessibility permissions */}
          {contextCollectionState ===
            CONTEXT_COLLECTOR_STATE.ACCESSIBILITY_PERMISSION_MISSING && (
            <DropdownMenu.Item
              onClick={handleRequestAccessibilityPermissions}
              disabled={isLoading}
            >
              {isLoading ? (
                <Button variant="ghost" disabled isLoading className="p-0" />
              ) : (
                <>Grant Permissions</>
              )}
            </DropdownMenu.Item>
          )}

          {/* Error states - show inline error reason + retry */}
          {(contextCollectionState ===
            CONTEXT_COLLECTOR_STATE.SOMETHING_WENT_WRONG ||
            contextCollectionState ===
              CONTEXT_COLLECTOR_STATE.UNABLE_TO_START ||
            contextCollectionState ===
              CONTEXT_COLLECTOR_STATE.UNABLE_TO_STOP) && (
            <YStack className="px-3 py-2 gap-2">
              <Text
                className="text-xs text-foreground-subtle leading-normal line-clamp-2"
                // Raw transport errors are kept on the title only so the menu
                // doesn't leak IPC / observer detail into the user-facing copy.
                title={status.error || error || undefined}
              >
                Context Awareness encountered an issue.
              </Text>
              <Button
                variant="secondary"
                size="dense"
                onClick={handleRetry}
                disabled={isLoading}
                isLoading={isLoading}
              >
                Retry
              </Button>
            </YStack>
          )}

          {/* Loading states - show disabled item */}
          {(contextCollectionState === CONTEXT_COLLECTOR_STATE.STARTING ||
            contextCollectionState === CONTEXT_COLLECTOR_STATE.STOPPING) && (
            <DropdownMenu.Item disabled>
              <Button variant="ghost" disabled isLoading className="p-0" />
              {contextCollectionStateDetails.text}
            </DropdownMenu.Item>
          )}

          {/* Manage app inclusions/exclusions */}
          {(contextCollectionState === CONTEXT_COLLECTOR_STATE.ENABLED ||
            contextCollectionState === CONTEXT_COLLECTOR_STATE.PAUSED_TIMED ||
            contextCollectionState === CONTEXT_COLLECTOR_STATE.STOPPED) && (
            <YStack className="pt-1">
              <Separator className="bg-border-strong w-full" />
              <XStack className="px-3 pt-3 pb-1 items-start justify-between gap-4">
                <Text className="text-xs text-foreground-subtle leading-normal">
                  Exclude apps and websites from context
                </Text>
                <Button
                  variant="secondary"
                  size="dense"
                  className="shrink-0"
                  onClick={() => {
                    setOpen(false);
                    setHash('settings/PrivacyControls');
                  }}
                >
                  <span className="leading-normal">Manage</span>
                </Button>
              </XStack>
            </YStack>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    );
  }
);

interface CollectionStateIconProps {
  details: ContextCollectionStateDetails;
  isPending: boolean;
}

const CollectionStateIcon = ({
  details,
  isPending,
}: CollectionStateIconProps) => {
  if (isPending) {
    return (
      <Icons.loader className="size-3.5 animate-spin text-foreground-muted" />
    );
  }
  if (details.icon) {
    return <details.icon className="size-3.5 text-foreground-subtle" />;
  }
  return (
    <div
      className={cn('size-2 mx-[3px] rounded-full shrink-0', details.iconColor)}
    />
  );
};

export { ContextCollectionMenu };
